import React, { useState, useEffect } from "react";
import {
  Sidebar,
  Menu,
  MenuItem,
  menuClasses,
} from "react-pro-sidebar";
import { Link } from "react-router-dom";

import "./sidebar.css";
import { loadData } from "../../../utils/localStorage";
import LogoutModal from "../../auth/logout/logout";
import {
  MAIN_LOGO_WHITE,
  MAIN_LOGO_SHIELD,
} from "../../../constant";
import { getAdminTabsInfo as getAdminSections, getStudentTabsInfo as getStudentSections } from "../../../utils/tabsUtils";

const SIDEBAR_BG     = "#1a2744";
const TEXT_COLOR     = "rgba(255,255,255,0.88)";
const ICON_COLOR     = "rgba(255,255,255,0.60)";
const HOVER_BG       = "rgba(255,255,255,0.07)";
const ACTIVE_BG      = "rgba(251,182,21,0.14)";
const ACTIVE_BORDER  = "#fbb615";
const DISABLED_COLOR = "rgba(255,255,255,0.28)";

const SideBar = ({ activeItem }) => {
  const userData = JSON.parse(loadData("user"));
  const permissionPolicy = userData?.role_permission?.permission_policy;
  const roleType = userData?.role_permission?.role_type;
  const isAdmin = roleType?.indexOf("SCHOOL") > -1;

  const sections = isAdmin
    ? getAdminSections(permissionPolicy)
    : getStudentSections(roleType);

  const [collapsed, setCollapsed] = useState(
    () => sessionStorage.getItem("sb_collapsed") === "true"
  );
  const [toggled, setToggled] = useState(false);
  const [broken, setBroken] = useState(false);
  const [logoutModal, setLogoutModal] = useState(false);

  useEffect(() => {
    document.documentElement.style.setProperty(
      "--sidebar-width",
      collapsed ? "80px" : "260px"
    );
    sessionStorage.setItem("sb_collapsed", String(collapsed));
  }, [collapsed]);

  const isActive = (label) => activeItem === label;

  const activeStyle = {
    backgroundColor: ACTIVE_BG,
    borderLeft: `3px solid ${ACTIVE_BORDER}`,
    color: "#ffffff",
  };

  const menuItemStyles = {
    root: {
      fontSize: "13.5px",
      fontWeight: 500,
      color: TEXT_COLOR,
    },
    icon: {
      color: ICON_COLOR,
      fontSize: "15px",
      [`&.${menuClasses.disabled}`]: { color: DISABLED_COLOR },
    },
    SubMenuExpandIcon: { color: "rgba(255,255,255,0.35)" },
    button: {
      borderRadius: "8px",
      margin: "1px 8px",
      width: "calc(100% - 16px)",
      transition: "background 0.15s",
      [`&.${menuClasses.disabled}`]: { color: DISABLED_COLOR },
      "&:hover": {
        backgroundColor: HOVER_BG,
        color: "#ffffff",
      },
    },
    label: ({ open }) => ({
      fontWeight: open ? 700 : 500,
      color: TEXT_COLOR,
    }),
  };

  const SectionHeader = ({ label }) =>
    collapsed ? null : (
      <div className="sb-section-header">
        <span>{label}</span>
      </div>
    );

  return (
    <>
      {LogoutModal({ logoutModal, handleCloseLogoutModal: () => setLogoutModal(false) })}
      <Sidebar
        collapsed={collapsed}
        toggled={toggled}
        onBackdropClick={() => setToggled(false)}
        onBreakPoint={setBroken}
        breakPoint="md"
        backgroundColor={SIDEBAR_BG}
        style={{ borderRight: "none", overflow: "visible" }}
      >
        <div className="sb-root">

          {/* ── Logo area ── */}
          <div className={`sb-logo-area ${collapsed ? "sb-logo-area--collapsed" : ""}`}>
            {!collapsed && (
              <div className="sb-logo-wrap">
                <img src={MAIN_LOGO_WHITE} alt="FRS logo" className="sb-logo-full" />
              </div>
            )}
            {collapsed && (
              <img src={MAIN_LOGO_SHIELD} alt="FRS" className="sb-logo-shield" />
            )}
            <button
              className="sb-collapse-btn"
              onClick={() => setCollapsed(!collapsed)}
              title={collapsed ? "Expand menu" : "Collapse menu"}
            >
              <i className={`bi ${collapsed ? "bi-chevron-double-right" : "bi-chevron-double-left"}`}></i>
            </button>
          </div>

          <div className="sb-divider" />

          {/* ── Scrollable menu ── */}
          <div className="sb-menu-area">
            <Menu menuItemStyles={menuItemStyles}>
              {sections.map((section, idx) => {
                if (section.canView === false) return null;
                const visibleItems = section.items.filter(
                  (item) => item.canView !== false
                );
                if (visibleItems.length === 0) return null;
                return (
                  <React.Fragment key={idx}>
                    <SectionHeader label={section.label} />
                    {visibleItems.map((item, itemIdx) => (
                      <MenuItem
                        key={itemIdx}
                        component={<Link to={item.route} />}
                        icon={<i className={`bi ${item.icon}`}></i>}
                        style={isActive(item.label) ? activeStyle : {}}
                      >
                        {item.label}
                      </MenuItem>
                    ))}
                  </React.Fragment>
                );
              })}
            </Menu>

            <div className="sb-divider sb-divider--spaced" />

            <Menu menuItemStyles={menuItemStyles}>
              <MenuItem
                icon={<i className="bi bi-box-arrow-right"></i>}
                onClick={() => setLogoutModal(true)}
              >
                Logout
              </MenuItem>
            </Menu>
          </div>

        </div>
      </Sidebar>
    </>
  );
};

export default SideBar;
